import React from 'react'
import AutoFitValue from './AutoFitValue'
import { money, fmtDay } from './formatting'
import { cutOffOf } from './legacy'

// ─────────────────────────────────────────────────────────────────────────────
//   Saldos heredados del Centralizador
//
//   Estas cifras NO se recalculan aquí: los leads que las originaron nunca se
//   migraron a este sistema, así que `declared` es una cifra declarada, no
//   derivada. Lo único vivo es `settled` — lo que se va saldando desde esta
//   pantalla.
//
//   La sección tiene que leerse como otra cosa de un vistazo, porque varias de
//   estas contrapartes aparecen también arriba con saldos distintos y sin
//   relación. Tres señales, para que no dependa sólo del color:
//     1. el badge "Legacy · <source>" en cada tarjeta, que lo dice con palabras;
//     2. la superficie hundida (--bg-raised sobre el --bg-surface de las vivas);
//     3. --purple como acento de sección, que no se usa en ninguna otra parte
//        de estas páginas.
//   Los importes en cambio conservan la semántica de color del resto de la
//   página (ámbar debido, verde saldado, rojo pendiente): lo que cambia es el
//   marco, no el significado de las cifras.
//
//   Un solo lado por página desde que Financial se partió: `/payables` monta
//   los payables heredados bajo los partners vivos, `/receivables` los
//   receivables bajo los buyers. El endpoint sigue siendo uno solo y devuelve
//   los dos lados — cada página se queda con su mitad.
// ─────────────────────────────────────────────────────────────────────────────
const LEGACY_SURFACE = {
  background: 'var(--bg-raised)',
  borderLeft: '3px solid var(--purple)',
  boxShadow: 'var(--shadow-sm)',
}

function LegacyBalanceCard({ balance, alsoLive, actionLabel, historyLabel, onRegister, onHistory }) {
  // Porcentaje de barra, no importe. Igual que en las tarjetas vivas.
  const settledPct = balance.declared > 0
    ? Math.min(100, Math.round((balance.settled / balance.declared) * 100))
    : 0

  return (
    <div className="card fade-in partners-card" style={LEGACY_SURFACE}>
      <div className="partners-card-head">
        <div className="partners-card-title-wrap">
          <div className="partners-card-title">{balance.name}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '6px 0 4px' }}>
            <span className="badge badge-purple">
              {balance.source ? `Legacy · ${balance.source}` : 'Legacy'}
            </span>
            {alsoLive && (
              <span
                className="badge badge-gray"
                title={`${balance.name} also has a live balance above on this page. The two are separate and unrelated: this one is what the previous system left open.`}
              >
                Also above
              </span>
            )}
          </div>
          <div className="partners-card-subtitle">{balance.legacy_id}</div>
        </div>
      </div>

      <div className="fin-card-metrics" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)', gap: 8, marginTop: 4 }}>
        <div className="partners-metric-box" style={{ minWidth: 0, overflow: 'hidden' }}>
          <AutoFitValue color="var(--amber)">{money(balance.declared)}</AutoFitValue>
          <div className="partners-metric-label">Declared</div>
        </div>
        <div className="partners-metric-box" style={{ minWidth: 0, overflow: 'hidden' }}>
          <AutoFitValue color="var(--green)">{money(balance.settled)}</AutoFitValue>
          <div className="partners-metric-label">Settled</div>
        </div>
        <div className="partners-metric-box" style={{ minWidth: 0, overflow: 'hidden' }}>
          <AutoFitValue color="var(--red)">{money(balance.pending)}</AutoFitValue>
          <div className="partners-metric-label">Pending</div>
        </div>
      </div>

      {balance.declared > 0 && (
        <div style={{ marginTop: 14 }}>
          {/* La pista va en --border y no en --bg-raised: la tarjeta heredada YA
              es --bg-raised, y la barra desaparecería sobre su propio fondo. */}
          <div style={{ height: 8, borderRadius: 4, background: 'var(--border)', overflow: 'hidden' }}>
            <div style={{ width: `${settledPct}%`, height: '100%', background: 'var(--green)' }} />
          </div>
        </div>
      )}

      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12, color: 'var(--text-muted)' }}>
        <span>As of {fmtDay(balance.as_of)}</span>
        {balance.note && <span>{balance.note}</span>}
      </div>

      <div className="fin-card-actions" style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button className="btn btn-primary btn-sm" style={{ flex: 1 }}
          onClick={() => onRegister(balance)}
          disabled={balance.declared <= 0}
          title={balance.declared <= 0 ? 'Nothing declared on this balance' : undefined}>
          {actionLabel}
        </button>
        <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => onHistory(balance)}>
          {historyLabel}
        </button>
      </div>
    </div>
  )
}

// Un bloque (el lado que le toca a esta página) con sus tres totales y sus
// tarjetas.
export function LegacyBlock({ title, caption, stats, balances, liveNames, actionLabel, historyLabel, onRegister, onHistory }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ marginBottom: 10 }}>
        <h3 style={{ fontSize: 15, margin: '0 0 2px' }}>{title}</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>{caption}</p>
      </div>

      <div className="fin-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
        {stats.map((st) => (
          <div key={st.label} className="card" style={{ ...LEGACY_SURFACE, padding: '18px 12px', textAlign: 'center', minWidth: 0, overflow: 'hidden' }}>
            <AutoFitValue color={st.color} max={24} min={13}>{money(st.value)}</AutoFitValue>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{st.label}</div>
          </div>
        ))}
      </div>

      {balances.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Nothing carried over on this side.</p>
      ) : (
        <div className="fin-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 16 }}>
          {balances.map((b) => (
            <LegacyBalanceCard
              key={b.id}
              balance={b}
              alsoLive={liveNames.has(String(b.name || '').trim().toLowerCase())}
              actionLabel={actionLabel}
              historyLabel={historyLabel}
              onRegister={onRegister}
              onHistory={onHistory}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function LegacyBalancesSection({
  balances, error, liveNames,
  title, caption, stats, actionLabel, historyLabel, onRegister, onHistory,
}) {
  // Nada heredado de este lado y ninguna carga fallida: la sección entera
  // sobra. Una cabecera sobre un sistema apagado con cero saldos es sólo
  // ruido. La regla es por lado desde que hay una página por lado: que el otro
  // lado tenga saldos no es razón para pintar una sección vacía en esta.
  if (!error && balances.length === 0) return null

  const { asOf, mixed } = cutOffOf(balances)

  return (
    <div style={{ marginTop: 40, borderTop: '1px solid var(--border)', paddingTop: 28 }}>
      <div style={{ marginBottom: 20 }}>
        <div className="hero-kicker" style={{ color: 'var(--purple)' }}>Carried over from the previous system</div>
        <h2 style={{ fontSize: 18, margin: '0 0 4px' }}>Legacy balances</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0, maxWidth: 720 }}>
          What the old system left open. These are declared figures, not calculated ones — those leads
          were never migrated here, so nothing above recomputes them. A counterparty can appear both
          here and above with two separate, unrelated balances.
        </p>

        <div style={{ marginTop: 10 }}>
          {/* La fecha sale de `cutOffOf` sobre las filas de ESTE lado, no del
              par `as_of`/`as_of_mixed` del envelope (que mezcla los dos) — ver
              el comentario en legacy.js. Cuando los saldos de este lado no
              comparten fecha de corte, enseñar una sola diría algo falso sobre
              el resto: se remite a la de cada tarjeta, que siempre es la suya. */}
          {mixed ? (
            <span className="badge badge-amber" title="These balances were cut off on different dates. Each card carries its own.">
              Mixed cut-off dates — see each balance
            </span>
          ) : asOf ? (
            <span className="badge badge-purple">As of {fmtDay(asOf)}</span>
          ) : null}
        </div>
      </div>

      {error ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          Legacy balances could not be loaded. The sections above are unaffected.
        </p>
      ) : (
        <LegacyBlock
          title={title}
          caption={caption}
          stats={stats}
          balances={balances}
          liveNames={liveNames}
          actionLabel={actionLabel}
          historyLabel={historyLabel}
          onRegister={onRegister}
          onHistory={onHistory}
        />
      )}
    </div>
  )
}
